// DCF Result 탭 — 바닐라 renderResult 의 FCFF PV / Terminal / Bridge 부분 이식.
// result JSON 소비 필드: fcff.fcff, pv.{pvif,pv_fcff,sum_pv_fcff,pv_terminal},
// terminal.{fcff_terminal_grown,fcff_terminal_input,g,wacc,tv,pv_tv},
// bridge.{sum_pv_fcff,pv_terminal,pv_tax_shield,operating_value,non_operating_assets,
//        interest_bearing_debt,preferred_stock_amount,equity_value,shares_outstanding,
//        price_per_share,diluted_shares_outstanding,diluted_price_per_share},
// meta.period_labels
import type { ReactNode } from "react";
import { fmt, pct } from "../../lib/format";
import { unitShare, useT, yearCol } from "../../lib/i18n";
import { useDcf } from "../../stores/dcf";

function BridgeRow({
  label,
  value,
  cls,
}: {
  label: string;
  value: ReactNode;
  cls?: string;
}) {
  const total = !!cls && cls.indexOf("total") >= 0;
  return (
    <div className={`bridge-row${total ? " total" : ""}`}>
      <span className="bridge-label">{label}</span>
      <span className={`bridge-val ${cls || ""}`}>{value}</span>
    </div>
  );
}

export default function ResultTab() {
  const { lang, t, tf } = useT();
  const current = useDcf((s) => s.current);
  if (!current) return null;
  // 주당가치만 원 단위라, 나머지 섹션이 백만원임을 제목마다 밝힌다 (사용자 요청)
  const unitHint = <span className="hint">{tf("hint.unit_m", { m: t("unit.money") })}</span>;
  const r = current.result;
  if (!r) {
    return (
      <div className="data-card">
        <h4>{t("h.fcff")} {unitHint}</h4>
        <p className="hint">{t("ph.recalc_show")}</p>
      </div>
    );
  }

  const labels =
    r.meta?.period_labels || [1, 2, 3, 4, 5].map((n) => yearCol(lang, n));
  const b = r.bridge || {};
  const tv = r.terminal || {};
  const pv = r.pv || {};
  const ff = r.fcff || {};

  const basicPps = b.price_per_share;
  const dilutedPps = b.diluted_price_per_share;
  const hasDiluted =
    dilutedPps != null && basicPps != null && Math.abs(dilutedPps - basicPps) > 1;

  return (
    <>
      <div className="data-card">
        <h4>{t("h.fcff")}</h4>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t("col.item")}</th>
              {labels.map((l) => (
                <th key={l}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>FCFF</td>
              {(ff.fcff || []).map((v, i) => (
                <td key={i}>{fmt(v)}</td>
              ))}
            </tr>
            <tr>
              <td>{t("row.discount_factor")}</td>
              {(pv.pvif || []).map((v, i) => (
                <td key={i}>{Number(v).toFixed(4)}</td>
              ))}
            </tr>
            <tr className="total">
              <td>{t("row.pv")}</td>
              {(pv.pv_fcff || []).map((v, i) => (
                <td key={i}>{fmt(v)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="data-card">
        <h4>Terminal Value {unitHint}</h4>
        <table className="data-table">
          <tbody>
            <tr>
              <td>{t("tv.terminal_fcff")}</td>
              <td>
                {fmt(
                  tv.fcff_terminal_grown != null
                    ? tv.fcff_terminal_grown
                    : tv.fcff_terminal_input,
                )}
              </td>
            </tr>
            <tr>
              <td>{t("tv.g")}</td>
              <td>{pct(tv.g)}</td>
            </tr>
            <tr>
              <td>WACC</td>
              <td>{pct(tv.wacc)}</td>
            </tr>
            <tr className="total">
              <td>Terminal Value</td>
              <td>{fmt(tv.tv)}</td>
            </tr>
            <tr className="total">
              <td>{t("tv.pv")}</td>
              <td>{fmt(tv.pv_tv != null ? tv.pv_tv : pv.pv_terminal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="data-card">
        <h4>Valuation details {unitHint}</h4>
        <div>
          <BridgeRow
            label={t("br.sum_pv")}
            value={fmt(pv.sum_pv_fcff != null ? pv.sum_pv_fcff : b.sum_pv_fcff)}
            cls="pos"
          />
          <BridgeRow
            label={t("br.tv_pv")}
            value={fmt(pv.pv_terminal != null ? pv.pv_terminal : b.pv_terminal)}
            cls="pos"
          />
          {!!b.pv_tax_shield && (
            <BridgeRow label={t("br.tax_shield")} value={fmt(b.pv_tax_shield)} cls="pos" />
          )}
          <BridgeRow label="Operating Value" value={fmt(b.operating_value)} cls="pos total" />
          <BridgeRow label={t("br.non_op")} value={fmt(b.non_operating_assets)} cls="pos" />
          <BridgeRow
            label={t("br.debt")}
            value={"-" + fmt(b.interest_bearing_debt)}
            cls="neg"
          />
          {!!b.preferred_stock_amount && (
            <BridgeRow
              label={t("br.pref")}
              value={"-" + fmt(b.preferred_stock_amount)}
              cls="neg"
            />
          )}
          <BridgeRow label={t("br.equity")} value={fmt(b.equity_value)} cls="total" />
          <BridgeRow
            label={t("br.shares")}
            value={fmt(b.shares_outstanding) + t("unit.shares_suffix")}
          />
          <BridgeRow
            label={t("br.pps")}
            value={fmt(basicPps) + unitShare(lang)}
            cls="total"
          />
          {hasDiluted && (
            <BridgeRow
              label={t("br.dil_shares")}
              value={fmt(b.diluted_shares_outstanding) + t("unit.shares_suffix")}
            />
          )}
          {hasDiluted && (
            <BridgeRow
              label={t("br.dil_pps")}
              value={fmt(dilutedPps) + unitShare(lang)}
              cls="total"
            />
          )}
        </div>
      </div>
    </>
  );
}
